import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCheckoutStatus, cancelPendingRegistration } from '@/api';
import AppLayout from '@/components/AppLayout';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function PaymentCallback() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, canceled, error
  const [message, setMessage] = useState('');
  const checkStarted = useRef(false);
  const isCompleted = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (checkStarted.current) return;
    checkStarted.current = true;

    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    console.log('PaymentCallback: starting', { sessionId, canceled });

    // If explicitly canceled
    if (canceled === 'true') {
      handleCancellation();
      return;
    }

    // If no session ID, show error
    if (!sessionId) {
      setStatus('error');
      setMessage('Sessione di pagamento non trovata.');
      return;
    }

    // Start checking payment
    checkPaymentStatus(sessionId, 0);

    async function checkPaymentStatus(sid, attempt) {
      // Don't continue if already completed
      if (isCompleted.current) return;
      
      console.log(`PaymentCallback: checking attempt ${attempt}`);

      try {
        const { data } = await getCheckoutStatus(sid);
        console.log('PaymentCallback: got status', data);

        // Check for successful payment (paid or trial with no_payment_required)
        if (data.payment_status === 'paid' || data.payment_status === 'no_payment_required' || data.status === 'complete') {
          handleSuccess();
          return;
        }

        // If session expired
        if (data.status === 'expired') {
          handleCancellation();
          return;
        }

        // If still pending and haven't reached max attempts
        if (attempt < 10) {
          setTimeout(() => checkPaymentStatus(sid, attempt + 1), 2000);
        } else {
          // Max attempts reached - show error with option to go to dashboard
          setStatus('error');
          setMessage('Impossibile verificare il pagamento. Se hai completato il pagamento, prova ad accedere alla dashboard.');
        }

      } catch (err) {
        console.error('PaymentCallback: error', err);

        // Auth error - clear all storage
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setStatus('error');
          setMessage('Sessione scaduta. Effettua nuovamente il login.');
          localStorage.clear();
          sessionStorage.clear();
          localStorage.setItem("servicego_app_version", "2.0.0");
          return;
        }

        // Retry on other errors (up to 3 times)
        if (attempt < 3) {
          setTimeout(() => checkPaymentStatus(sid, attempt + 1), 2000);
        } else {
          setStatus('error');
          setMessage('Errore durante la verifica. Contatta il supporto se hai completato il pagamento.');
        }
      }
    }

    function handleSuccess() {
      if (isCompleted.current) return;
      isCompleted.current = true;

      console.log('PaymentCallback: SUCCESS - redirecting to dashboard');
      setStatus('success');
      setMessage('Il tuo abbonamento è attivo!');
      toast.success('Pagamento completato!');

      // Redirect to dashboard
      setTimeout(() => {
        window.location.href = '/ditta/dashboard';
      }, 1500);
    }

    async function handleCancellation() {
      if (isCompleted.current) return;
      
      console.log('PaymentCallback: handling cancellation');

      try {
        const response = await cancelPendingRegistration();

        // Backend might have activated the subscription
        if (response.data?.activated) {
          handleSuccess();
          return;
        }

        isCompleted.current = true;
        setStatus('canceled');
        setMessage('Pagamento annullato. La registrazione non è stata completata.');
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem("servicego_app_version", "2.0.0");

      } catch (err) {
        console.error('Error canceling:', err);

        // If subscription is already active
        if (err?.response?.data?.detail?.includes('attivo')) {
          handleSuccess();
          return;
        }

        isCompleted.current = true;
        setStatus('canceled');
        setMessage('Pagamento annullato.');
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem("servicego_app_version", "2.0.0");
      }
    }

  }, []); // Empty dependency array - run only once

  return (
    <AppLayout>
      <Toaster />
      <div className="max-w-md mx-auto text-center py-12 px-4">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-indigo-600 animate-spin mb-4" />
            <h1 className="text-xl font-semibold mb-2">Verifica pagamento...</h1>
            <p className="text-gray-600">Attendere prego...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h1 className="text-xl font-semibold mb-2 text-green-700">Pagamento completato!</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Reindirizzamento alla dashboard...</p>
          </>
        )}

        {status === 'canceled' && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h1 className="text-xl font-semibold mb-2 text-red-700">Pagamento annullato</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.href = '/ditta/registrazione'}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Riprova la registrazione
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                Torna alla home
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-orange-500 mb-4" />
            <h1 className="text-xl font-semibold mb-2 text-orange-700">Errore</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.href = '/ditta/dashboard'}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Vai alla Dashboard
              </Button>
              <Button 
                onClick={() => window.location.href = '/login?role=company'}
                variant="outline"
                className="w-full"
              >
                Effettua il Login
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                Torna alla home
              </Button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
